/**
 * useCountUp — React hook for animated number rollups.
 *
 * Counts from 0 to `to` over 1.4s with easeOutCubic when the target
 * element enters the viewport. One-shot. Respects prefers-reduced-motion.
 *
 * Usage:
 *   const { ref, value } = useCountUp(47, { suffix: "K" });
 *   return <span ref={ref}>{value}</span>;
 *
 *   // Comma format for large numbers:
 *   const stat = useCountUp(215000, { format: "comma" });
 *
 *   // Decimal:
 *   const x = useCountUp(3.6, { decimals: 1, suffix: "×" });
 *
 *   // Currency:
 *   const price = useCountUp(49, { prefix: "$" });
 *
 * Provenance: paperclip/context/pattern-picker/COMPETITOR_REVIEW_TRYMAKO_2026-05-13.md §9d
 */
import { useEffect, useRef, useState } from "react";

export type UseCountUpOptions = {
  /** Suffix appended to the value, e.g. "K", "×", "%". */
  suffix?: string;
  /** Prefix prepended to the value, e.g. "$". */
  prefix?: string;
  /** Decimal places. Default 0. */
  decimals?: number;
  /** Number formatting. Default raw integer; "comma" adds thousands separators. */
  format?: "raw" | "comma";
  /** Animation duration in ms. Default 1400. */
  duration?: number;
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function useCountUp<T extends HTMLElement = HTMLSpanElement>(
  to: number,
  options: UseCountUpOptions = {}
) {
  const ref = useRef<T | null>(null);
  const [value, setValue] = useState<string>(() => formatNumber(0, options));

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setValue(formatNumber(to, options));
      return;
    }

    const play = () => {
      const start = performance.now();
      const dur = options.duration ?? 1400;
      let rafId = 0;
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / dur);
        setValue(formatNumber(to * easeOutCubic(t), options));
        if (t < 1) rafId = requestAnimationFrame(step);
        else setValue(formatNumber(to, options)); // pin to exact value
      };
      rafId = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafId);
    };

    if (!("IntersectionObserver" in window)) {
      return play();
    }

    let cancel: (() => void) | undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cancel = play();
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (cancel) cancel();
    };
    // Re-run when `to` or formatting changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, options.suffix, options.prefix, options.decimals, options.format, options.duration]);

  return { ref, value };
}

function formatNumber(raw: number, options: UseCountUpOptions): string {
  const { suffix = "", prefix = "", decimals = 0, format = "raw" } = options;
  let body: string;
  if (decimals > 0) {
    body = Number(raw).toFixed(decimals);
  } else if (format === "comma") {
    body = Math.round(raw).toLocaleString();
  } else {
    body = String(Math.round(raw));
  }
  return prefix + body + suffix;
}
